// @md/shared
// Shared types consumed by backend, frontend, and extension.
// After `supabase db push`, run `pnpm --filter @md/shared gen:types` to regenerate
// the full DB type surface into ./database.types.ts.

export const PACKAGE_NAME = '@md/shared';

// ─── Channel discriminator ─────────────────────────────────────────────────
// Mirrors the `channel_t` Postgres enum in supabase/migrations/20260417120000_init.sql.
export const CHANNELS = [
  'gmail',
  'etsy_md',
  'etsy_hk',
  'amazon',
  'shopify_md',
  'shopify_brokerage',
  // ShipStation is not a message channel — it's the cross-store order-truth
  // source. It's in this enum only so api_credentials can hold its auth
  // (channel='shipstation', label='main'). Never in INGEST or SEND lists.
  'shipstation',
] as const;
export type Channel = (typeof CHANNELS)[number];

// Channels we ingest messages from (Shopify is order-context only, no message ingest).
export const INGEST_CHANNELS: readonly Channel[] = ['gmail', 'etsy_md', 'etsy_hk', 'amazon'];
// Channels with native API send support.
export const API_SEND_CHANNELS: readonly Channel[] = ['gmail', 'amazon'];
// Channels that send via the browser extension bridge.
export const EXTENSION_SEND_CHANNELS: readonly Channel[] = ['etsy_md', 'etsy_hk'];

// ─── Brand ─────────────────────────────────────────────────────────────────
// Drafter switches voice based on this. HK-forwarded emails + HK Etsy threads → 'hk'.
export type Brand = 'md' | 'hk';

// ─── Conversation status lifecycle (Phase 5a) ──────────────────────────────
//
// open      — needs a response. New inbound threads, or anything a new
//             inbound message has arrived on (other than spam).
// awaiting  — we replied, waiting on the customer.
// snoozed   — manually snoozed; has a snoozed_until timestamp. A cron
//             worker flips snoozed → awaiting when snoozed_until <= now().
// resolved  — closed out; kept for history and for the "For the Record" bucket.
// spam      — junk. Hidden from all active buckets. New inbound messages on
//             a spam thread do NOT flip it back to open — only a manual
//             status change can leave spam.
export const CONVERSATION_STATUSES = [
  'open',
  'awaiting',
  'snoozed',
  'resolved',
  'spam',
] as const;

export type ConversationStatus = (typeof CONVERSATION_STATUSES)[number];

// Active = shows up in work-oriented buckets (not resolved, not spam).
export const ACTIVE_STATUSES = ['open', 'awaiting', 'snoozed'] as const satisfies readonly ConversationStatus[];

// Snooze presets surfaced in the SnoozeMenu. Values are minute offsets
// from "now"; the backend stamps snoozed_until = now() + offset.
export const SNOOZE_PRESETS = [
  { label: 'Later today', minutes: 4 * 60 },
  { label: 'Tomorrow morning', minutes: 20 * 60 },
  { label: 'In 2 days', minutes: 48 * 60 },
  { label: 'Next week', minutes: 7 * 24 * 60 },
] as const;

export type SnoozePresetLabel = (typeof SNOOZE_PRESETS)[number]['label'];

// ─── Message direction ─────────────────────────────────────────────────────
export type Direction = 'inbound' | 'outbound';

// ─── User role ─────────────────────────────────────────────────────────────
export type UserRole = 'owner' | 'viewer';

// ─── Draft source ──────────────────────────────────────────────────────────
// Discriminates AI-generated drafts from user-edited versions in the draft history.
export type DraftSource = 'ai_generated' | 'user_edited';
